import { z } from 'zod';
import { BIOME_IDS, BIOMES } from './biomes.js';
import { bossOfBiome } from './bosses.js';
/**
 * The hunting log (ADR 0093): one page per creature of a region, its elites
 * and its boss, filled by hunting it. Its count is the career's kills of
 * that mob, so the log is per character and every old kill is already in it.
 */
export const BestiaryTierSchema = z.object({
    /** Kills of the page's creature that reach the tier. */
    kills: z.number().int().min(1),
    /** Extra damage dealt to that creature, as a fraction (0.01 = +1 %). */
    damageBonus: z.number().min(0).max(0.05),
});
/**
 * Kept tiny on purpose: at most +3 % against one species, under the level
 * gap's 10 % a level and the critical hit, so the pacing (ADR 0031) stays.
 */
export const BESTIARY_TIERS = Object.freeze([
    { kills: 10, damageBonus: 0.01 },
    { kills: 100, damageBonus: 0.02 },
    { kills: 1000, damageBonus: 0.03 },
].map((tier) => BestiaryTierSchema.parse(tier)));
/**
 * Distinct items remembered per page: a creature's table and its biome's
 * pieces stay well under it, and the career row stays small.
 */
export const BESTIARY_LOOT_MEMORY = 32;
export const BESTIARY_KINDS = ['creature', 'elite', 'boss'];
/** Region by region, in the world's order: the den creatures, the elites, the boss. */
export const BESTIARY_PAGES = Object.freeze(BIOME_IDS.flatMap((biome) => {
    const { dens, elites } = BIOMES[biome];
    return [
        ...dens.map((den) => ({ mob: den.mob, biome, kind: 'creature' })),
        ...[elites.rare, elites.epic, elites.legendary].map((mob) => ({
            mob,
            biome,
            kind: 'elite',
        })),
        { mob: bossOfBiome(biome).id, biome, kind: 'boss' },
    ];
}));
const BY_MOB = new Map(BESTIARY_PAGES.map((page) => [page.mob, page]));
/** The page of a mob id, if the log has one (event raiders, quarries and villagers have none). */
export function getBestiaryPage(mobId) {
    return BY_MOB.get(mobId);
}
//# sourceMappingURL=bestiary.js.map